import { zodResolver } from "@hookform/resolvers/zod";
import type { Resolver } from "react-hook-form";
import { z } from "zod";

export const representativeMatterSchema = z.object({
  text: z.string().min(1, "Matter description is required"),
});

const teamSizeSchema = z
  .number({ error: "Must be a number" })
  .int("Must be a whole number")
  .min(0, "Cannot be negative")
  .nullable();

export const translationsFormSchema = z.object({
  commentary: z.object({ postContent: z.string(), clerksCommentary: z.string() }),
  matters: z.record(z.string(), z.string()),
});

export type TranslationsFormValues = z.infer<typeof translationsFormSchema>;

export const orgFormSchema = z.object({
  // A matter's kind is derived from which group it sits in rather than a per-row flag: summaries,
  // publishable matters, confidential matters. The mapper splits the loaded matters into these on
  // load and recombines them into one positioned list on save.
  matterSummaries: z.array(representativeMatterSchema),
  publishableMatters: z.array(representativeMatterSchema),
  confidentialMatters: z.array(representativeMatterSchema),
  practiceHeads: z.array(z.string()),
  otherKeyLawyers: z.array(z.string()),
  // A client's kind is the group it sits in, not a per-row flag: publishable clients live in
  // keyClients, confidential clients in confidentialClients. The mapper splits the loaded clients
  // into these on load and recombines them into one positioned list on save.
  keyClients: z.array(z.string()),
  confidentialClients: z.array(z.string()),
  sarScores: z.record(z.string(), z.number().min(0).max(10).nullable()),
  postContent: z.string(),
  clerksCommentary: z.string(),
  teamSizePartners: teamSizeSchema,
  teamSizeLawyers: teamSizeSchema,
  editorCommentBox: z.string(),
  researcherNotes: z.string(),
  rankings: z.record(
    z.string(),
    z.object({
      rankingId: z.string().nullable(),
      tier: z.number().min(1).max(8).nullable(),
      status: z.enum(["draft", "pending", "published", "archived"]),
      ranked: z.boolean(),
      comment: z.string(),
      displayOrder: z.number(),
    }),
  ),
  translations: translationsFormSchema,
});

export type OrgFormValues = z.infer<typeof orgFormSchema>;

// react-hook-form registers Controller paths for slider keys that may not be in the form's
// default sarScores record yet (the template loads after the form is created). Those keys
// start as undefined, which z.number().nullable() rejects. This resolver normalises them to
// null before zod runs, so validation sees null (accepted) rather than undefined (rejected).
const inner = zodResolver(orgFormSchema);
export const orgFormResolver: Resolver<OrgFormValues> = (values, context, options) => {
  const sarScores = values.sarScores as Record<string, unknown>;
  const normalised: Record<string, number | null> = {};
  for (const [key, value] of Object.entries(sarScores)) {
    normalised[key] = typeof value === "number" ? value : null;
  }
  return inner({ ...values, sarScores: normalised }, context, options);
};
